import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from "@angular/core"
import {CaseSearchDataSource} from "./case-search-data-source"
import {CaseSearchService} from "src/app/_services/incident/case-search/case-search.service"
import {DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS} from "@angular/material/core"
import {distinctUntilChanged, takeUntil} from "rxjs/operators"
import {ExportService} from "src/app/_services/shared/export.service"
import {FormControl, FormGroup} from "@angular/forms"
import {GeographicAreasService} from "src/app/_services/shared/geographic-areas/geographic-areas.service"
import {irwinIdValidator} from "src/app/_validators/irwin-id-validator"
import {JurisdictionalAgenciesService} from "src/app/_services/shared/jurisdictionalAgencies/jurisdictional-agencies.service"
import {MatPaginator} from "@angular/material/paginator"
import {MatSort} from "@angular/material/sort"
import {MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS} from "@angular/material-moment-adapter"
import {Router} from "@angular/router"
import {RouterLinkBuilderService} from "src/app/_services/router-link-builder.service"
import {Subject} from "rxjs"
import {UnitService} from "src/app/_services/incident/case-search/unit/unit.service"
import {UnixToMomentDatePipe} from "src/app/_helpers/pipes/unix-to-moment-date.pipe"
import {WfdssCaseSearchDTO} from "src/app/_types/wfdss-case-search-dto"
import moment, {Moment} from "moment"
@Component({
	selector: "wfdss-case-search-report",
	templateUrl: "./wfdss-case-search-report.component.html",
	styleUrl: "./wfdss-case-search-report.component.scss",
	providers: [
		{provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]},
		{
			provide: MAT_DATE_FORMATS,
			useValue: {
				parse: {
					dateInput: "MM-DD-YYYY",
				},
				display: {
					dateInput: "l",
					monthYearLabel: "MMM YYYY",
					dateA11yLabel: "LL",
					monthYearA11yLabel: "MMMM YYYY",
				},
			},
		},
	],
	standalone: false,
})
export class WfdssCaseSearchReportComponent implements OnInit, OnDestroy, AfterViewInit {
	constructor(
		private gaccService: GeographicAreasService,
		private caseSearchSvc: CaseSearchService,
		private agenciesSvc: JurisdictionalAgenciesService,
		private unitSvc: UnitService,
		private linkBuilder: RouterLinkBuilderService,
		private router: Router,
		private exportSvc: ExportService,
	) {}
	@ViewChild(MatPaginator) paginator: MatPaginator
	@ViewChild(MatSort) sort: MatSort
	_destroying = new Subject<void>()
	radiosFg = new FormGroup({
		discoveryDateRadio: new FormControl<"Any" | "Discovered">({value: "Any", disabled: false}),
		outDateRadio: new FormControl<"Any" | "Out" | "None">({value: "Any", disabled: false}),
	})
	optionsFg = new FormGroup<any>({
		incidentSizeType: new FormControl("Greater than"),
		estimatedFinalCostType: new FormControl("Greater than"),
		discoveryDateDropdown: new FormControl({value: "On", disabled: true}),
		outDateDropdown: new FormControl({value: "On", disabled: true}),
	})
	/** @description Keys match the backend filter object. Types mostly match, with the following exceptions: 
	 * 	moments(convert to epoch) 
	 * 	geoId(put id inside array) 
	 * 	pooStates(put in array)  
	 * */
	filterFg = new FormGroup<ICaseAdvancedSearchFilterFg>({
		irwinId: new FormControl<string>(null, [irwinIdValidator]),
		discoveredBetweenMin: new FormControl<Moment>({value: moment("2025-03-25T05:55"), disabled: true}),
		discoveredBetweenMax: new FormControl<Moment>({value: moment("2025-03-25T05:55"), disabled: true}),
		discoveryDateOn: new FormControl<Moment>({value: moment("2025-03-25T05:55"), disabled: true}),
		fireOutBetweenMin: new FormControl<Moment>({value: moment("2025-03-25T05:55"), disabled: true}),
		fireOutBetweenMax: new FormControl<Moment>({value: moment("2025-03-25T05:55"), disabled: true}),
		fireOutDateOn: new FormControl<Moment>({value: moment("2025-03-25T05:55"), disabled: true}),
		fireOutDateNone: new FormControl<boolean>({value: false, disabled: false}),
		incidentSizeMin: new FormControl<number>({value: null, disabled: false}),
		incidentSizeMax: new FormControl<number>({value: null, disabled: false}),
		estimatedFinalCostMin: new FormControl<number>({value: null, disabled: false}),
		estimatedFinalCostMax: new FormControl<number>({value: null, disabled: false}),
		jurisdicitonalAgencyAtPooId: new FormControl<number>(null),
		jurisdicitonalUnitId: new FormControl<number>(null),
		geographicAreaIds: new FormControl<number>(null),
		protectingUnitId: new FormControl<number>(null),
		uniqueFireIdentifier: new FormControl<string>(null),
		incidentName: new FormControl<string>(null),
		pooStates: new FormControl<string>(null),
	})
	sizeAndCostDropdownOptions = [
		{value: "Less than", label: "Less than or equal to"}, 
		{value: "Greater than", label: "Greater than or equal to"}, 
		{value: "Between", label: "Between"}
	]
	dateDropdownOptions = ["On", "Before", "After", "Between"]
	outDateRangeValues = ["On", "Before", "After", "Between"]
	hideField = {
		incidentSizeMin: false,
		incidentSizeMax: true,
		incidentSizeAnd: true,
		estimatedFinalCostMin: false,
		estimatedFinalCostMax: true,
		estimatedFinalCostAnd: true,
		discoveredBetweenMin: true,
		discoveredBetweenMax: true,
		discoveryDateOn: false,
		fireOutBetweenMin: true,
		fireOutBetweenMax: true,
		fireOutDateOn: false,
	}
	defaultPageSize = 10
	sortActive = "Incident Name"
	sortDirection = "asc"
	fixedLayout = false
	advancedCaseSearchFilterObject: ICaseAdvancedSearchFilters = this.getEmptyFilterObject()
	whiteSpaceOnlyRegEx = new RegExp(/^\s+$/)
	numbersOnlyRegEx = new RegExp(/[0-9]/)
	searchParams: ICaseAdvancedSearchParams = {
		offset: 0,
		limit: this.defaultPageSize,
		sortDirection: "asc",
		sortOnField: "name",
	}
	tableDataSource = new CaseSearchDataSource(this.caseSearchSvc, this.getEmptyFilterObject(), this.searchParams)
	columnNameToPropertyMap = new Map<string, string>([
		["Incident Name", "name"],
		["Unique Fire Identifier", "uniqueFireIdentifier"],
		["Geographic Area", "geographicArea.shortName"],
		["Jurisdiction(s)", "jurisdictionalAgencyAtPoO.shortName"],
		["Protecting Unit", "protectingUnitAtPoO.unitIrwinId"],
		["State", "pooState"],
		["Size (Acres)", "incidentSize"],
		["Discovery Date/Time", "fireDiscoveryDateTime"],
	])
	columns = ["Incident Name", "Unique Fire Identifier", "Geographic Area", "Jurisdiction(s)", "Protecting Unit", "State", "Size (Acres)", "Discovery Date/Time"]
	geographicAreasList$ = this.gaccService.getAllGeographicAreas()
	statesList$ = this.caseSearchSvc.getStatesList()
	agenciesList$ = this.agenciesSvc.getJurisdictionalAgencies()
	unitsList$ = this.unitSvc.getVerboseUnits()

	ngOnInit(): void {
		/**
		 * These FormGroups and their valueChanges streams are separated for two reasons
		 * 1.) Prevent infinite loops eg... dropdown > updates filter > updates dropdown
		 * 2.) Allow filterFg to match the keys(and some value types) of the backend. 
		 */
		this.filterFg.valueChanges.pipe(takeUntil(this._destroying)).subscribe((caseFilterValue) => {
			this.createFilterObjectFromFormValue(caseFilterValue)
		})
		this.radiosFg
			.get("discoveryDateRadio")
			.valueChanges.pipe(takeUntil(this._destroying))
			.subscribe((discoveryDate) => {
				if (discoveryDate == "Discovered") {
					this.optionsFg.get("discoveryDateDropdown").enable()
					this.onOptionsDropdownChange("Discovery Date", this.optionsFg.get("discoveryDateDropdown").value)
				} else if (discoveryDate == "Any") {
					this.optionsFg.get("discoveryDateDropdown").disable()
					this.filterFg.get("discoveredBetweenMin").disable()
					this.filterFg.get("discoveredBetweenMax").disable()
					this.filterFg.get("discoveryDateOn").disable()
				}
			})
		this.radiosFg
			.get("outDateRadio")
			.valueChanges.pipe(takeUntil(this._destroying))
			.subscribe((outDateRadio) => {
				if (outDateRadio == "Out") {
					this.optionsFg.get("outDateDropdown").enable()
					this.onOptionsDropdownChange("Out Date", this.optionsFg.get("outDateDropdown").value)
				} else if (outDateRadio == "Any" || "None") {
					this.optionsFg.get("outDateDropdown").disable()
					this.filterFg.get("fireOutBetweenMin").disable()
					this.filterFg.get("fireOutBetweenMax").disable()
					this.filterFg.get("fireOutDateOn").disable()
				}
				if (outDateRadio == "None") {
					this.filterFg.get("fireOutDateNone").setValue(true)
				} else {
					this.filterFg.get("fireOutDateNone").setValue(false)
				}
			})
		this.optionsFg
			.get("discoveryDateDropdown")
			.valueChanges.pipe(distinctUntilChanged(), takeUntil(this._destroying))
			.subscribe((discoveryDateDropdown) => {
				this.onOptionsDropdownChange("Discovery Date", discoveryDateDropdown)
			})
		this.optionsFg
			.get("outDateDropdown")
			.valueChanges.pipe(distinctUntilChanged(), takeUntil(this._destroying))
			.subscribe((outDateDropdown) => {
				this.onOptionsDropdownChange("Out Date", outDateDropdown)
			})
		this.optionsFg
			.get("incidentSizeType")
			.valueChanges.pipe(distinctUntilChanged(), takeUntil(this._destroying))
			.subscribe((type) => {
				if (type == "Less than") {
					this.filterFg.get("incidentSizeMin").disable()
					this.filterFg.get("incidentSizeMax").enable()
					this.hideField.incidentSizeMax = false
					this.hideField.incidentSizeMin = true
					this.hideField.incidentSizeAnd = true
				}
				if (type == "Greater than") {
					this.filterFg.get("incidentSizeMin").enable()
					this.filterFg.get("incidentSizeMax").disable()
					this.hideField.incidentSizeMax = true
					this.hideField.incidentSizeMin = false
					this.hideField.incidentSizeAnd = true
				}
				if (type == "Between") {
					this.filterFg.get("incidentSizeMin").enable()
					this.filterFg.get("incidentSizeMax").enable()
					this.hideField.incidentSizeMax = false
					this.hideField.incidentSizeMin = false
					this.hideField.incidentSizeAnd = false
				}
				if (type == null) {
					this.filterFg.get("incidentSizeMin").disable()
					this.filterFg.get("incidentSizeMax").disable()
					this.hideField.incidentSizeMax = false
					this.hideField.incidentSizeMin = true
					this.hideField.incidentSizeAnd = true
				}
			})
		this.optionsFg
			.get("estimatedFinalCostType")
			.valueChanges.pipe(distinctUntilChanged(), takeUntil(this._destroying))
			.subscribe((type) => {
				if (type == "Less than") {
					this.filterFg.get("estimatedFinalCostMin").disable()
					this.filterFg.get("estimatedFinalCostMax").enable()
					this.hideField.estimatedFinalCostMax = false
					this.hideField.estimatedFinalCostMin = true
					this.hideField.estimatedFinalCostAnd = true
				}
				if (type == "Greater than") {
					this.filterFg.get("estimatedFinalCostMin").enable()
					this.filterFg.get("estimatedFinalCostMax").disable()
					this.hideField.estimatedFinalCostMax = true
					this.hideField.estimatedFinalCostMin = false
					this.hideField.estimatedFinalCostAnd = true
				}
				if (type == "Between") {
					this.filterFg.get("estimatedFinalCostMin").enable()
					this.filterFg.get("estimatedFinalCostMax").enable()
					this.hideField.estimatedFinalCostMax = false
					this.hideField.estimatedFinalCostMin = false
					this.hideField.estimatedFinalCostAnd = false
				}
				if (type == null) {
					this.filterFg.get("estimatedFinalCostMin").disable()
					this.filterFg.get("estimatedFinalCostMax").disable()
					this.hideField.estimatedFinalCostMax = false
					this.hideField.estimatedFinalCostMin = true
					this.hideField.estimatedFinalCostAnd = true
				}
			})
	}
	ngAfterViewInit(): void {
		this.tableDataSource.page$.pipe(takeUntil(this._destroying)).subscribe((page) => {
			this.paginator.length = page
			this.paginator.firstPage()
		})
		this.paginator.page.pipe(distinctUntilChanged(), takeUntil(this._destroying)).subscribe((pagiChange) => {
			this.searchParams.limit = pagiChange.pageSize
			this.searchParams.offset = pagiChange.pageIndex
			this.tableDataSource.onPageOrSortChange(this.searchParams)
		})
		this.sort.sortChange.pipe(distinctUntilChanged(), takeUntil(this._destroying)).subscribe((sortChange) => {
			this.searchParams.sortDirection = sortChange.direction
			this.searchParams.sortOnField = this.columnNameToPropertyMap.get(sortChange.active)
			this.tableDataSource.onPageOrSortChange(this.searchParams)
		})
	}

	onOptionsDropdownChange(outOrDiscovery: "Out Date" | "Discovery Date", dropdownValue: "Before" | "On" | "After" | "Between") {
		let hideMin, hideMax, hideOn, hideAnd
		switch (dropdownValue) {
			case "Before":
				hideMin = true
				hideMax = false
				hideOn = true
				break
			case "On":
				hideMin = true
				hideMax = true
				hideOn = false
				break
			case "After":
				hideMin = false
				hideMax = true
				hideOn = true
				break
			case "Between":
				hideMin = false
				hideMax = false
				hideOn = true
				break
		}
		if (outOrDiscovery == "Discovery Date") {
			this.hideField.discoveredBetweenMin = hideMin
			this.hideField.discoveredBetweenMax = hideMax
			this.hideField.discoveryDateOn = hideOn
			this.enableOrDisableDateControls(outOrDiscovery, !hideMin, !hideMax, !hideOn)
		} else if (outOrDiscovery == "Out Date") {
			this.hideField.fireOutBetweenMin = hideMin
			this.hideField.fireOutBetweenMax = hideMax
			this.hideField.fireOutDateOn = hideOn
			this.enableOrDisableDateControls(outOrDiscovery, !hideMin, !hideMax, !hideOn)
		}
	}
	/** @description when a date filter(optionsFg) dropdown changes value, use this function to enable or disable the relevant(filterFg) controls.*/
	enableOrDisableDateControls(outOrDiscovery: "Out Date" | "Discovery Date", min: boolean, max: boolean, on: boolean) {
		let minControl, maxControl, onControl
		if (outOrDiscovery == "Discovery Date") {
			minControl = "discoveredBetweenMin"
			maxControl = "discoveredBetweenMax"
			onControl = "discoveryDateOn"
		} else if (outOrDiscovery == "Out Date") {
			minControl = "fireOutBetweenMin"
			maxControl = "fireOutBetweenMax"
			onControl = "fireOutDateOn"
		}
		if (min && !max && !on) {
			this.filterFg.get(minControl).enable()
			this.filterFg.get(maxControl).disable()
			this.filterFg.get(onControl).disable()
		} else if (!min && max && !on) {
			this.filterFg.get(minControl).disable()
			this.filterFg.get(maxControl).enable()
			this.filterFg.get(onControl).disable()
		} else if (min && max && !on) {
			this.filterFg.get(minControl).enable()
			this.filterFg.get(maxControl).enable()
			this.filterFg.get(onControl).disable()
		} else if (on) {
			this.filterFg.get(minControl).disable()
			this.filterFg.get(maxControl).disable()
			this.filterFg.get(onControl).enable()
		}
	}
	/**@description takes the filterFg value and transforms it to an ICaseAdvancedSearchFilters for the backend*/
	createFilterObjectFromFormValue(formValue) {
		formValue.discoveryDateOn ? formValue.discoveryDateOn.set("hour", 12) : null
		formValue.fireOutDateOn ? formValue.fireOutDateOn.set("hour", 12) : null
		formValue.discoveredBetweenMax ? formValue.discoveredBetweenMax.startOf("day") : null
		formValue.discoveredBetweenMin ? formValue.discoveredBetweenMin.endOf("day") : null
		if (formValue.discoveredBetweenMin && formValue.discoveredBetweenMax) {
			formValue.discoveredBetweenMin.startOf("day")
			formValue.discoveredBetweenMax.endOf("day")
		}
		formValue.fireOutBetweenMax ? formValue.fireOutBetweenMax.startOf("day") : null
		formValue.fireOutBetweenMin ? formValue.fireOutBetweenMin.endOf("day") : null
		if (formValue.fireOutBetweenMin && formValue.fireOutBetweenMax) {
			formValue.fireOutBetweenMin = formValue.fireOutBetweenMin.startOf("day")
			formValue.fireOutBetweenMax.endOf("day")
		}
		let searchObject: ICaseAdvancedSearchFilters = this.getEmptyFilterObject()
		Object.assign(searchObject, formValue)
		let formattedSearchFilter = Object.entries(searchObject).map((keyVal) => {
			if (moment.isMoment(keyVal[1])) {
				keyVal[1] = keyVal[1].valueOf()
			}
			if (this.whiteSpaceOnlyRegEx.test(keyVal[1])) {
				keyVal[1] = null
			}
			if (keyVal[0] == "geographicAreaIds" && keyVal[1] != null) {
				keyVal[1] = [keyVal[1]] // backend needs array of geo ids, frontend only supports a single select currently
			}
			if (keyVal[0] == "pooStates" && keyVal[1] != null) {
				keyVal[1] = [keyVal[1]] // same as above
			}
			if (keyVal[0] == "estimatedFinalCostMax" || keyVal[0] == "estimatedFinalCostMin") {
				keyVal[1] = keyVal[1]?.replace(/\D/g, "")
				if (keyVal[1]?.length > 0) {
					keyVal[1] = Number.parseInt(keyVal[1])
				} else {
					keyVal[1] = null
				}
			}
			return [keyVal[0], keyVal[1]]
		})
		let outVal: ICaseAdvancedSearchFilters = Object.fromEntries(formattedSearchFilter)
		this.advancedCaseSearchFilterObject = outVal
	}
	onSearch() {
		this.tableDataSource.onSearch(this.advancedCaseSearchFilterObject, this.searchParams)
	}
	onClickIncident(row: WfdssCaseSearchDTO) {
		let link = this.linkBuilder.getIncidentInfoLink(row.irwinId)
		this.router.navigateByUrl(link)
	}
	ngOnDestroy(): void {
		this._destroying.next()
	}
	getEmptyFilterObject() {
		return Object.assign({}, searchObject)
	}
	download() {
		this.tableDataSource.loadCasesForDownload().subscribe((downloadCases) => {
			let casesData: WfdssCaseSearchDTO[] = downloadCases
			let datePipe = new UnixToMomentDatePipe()
			let exportDataArray = casesData.map((row) => {
				let humanReadableExportData: any = {
					"Incident Name": row?.name,
					"Unique Fire Identifier": row?.uniqueFireIdentifier,
					"Geographic Area": row?.geographicArea?.shortName,
					"Jurisdictional Agency(at Point of Origin)": row?.jurisdictionalAgencyAtPoO?.shortName,
					"Protecting Unit(at Point of Origin)": row?.protectingUnitAtPoO?.unitIrwinId + " - " + row?.protectingUnitAtPoO?.name,
					"State(at Point of Origin)": row?.pooState,
					"Size (Acres)": row.incidentSize,
					"Discovery Date/Time": datePipe.transform(row.fireDiscoveryDateTime, "MM/DD/YYYY HH:mm"),
					"Fire Out Date/Time": datePipe.transform(row.fireOutDateTime, "MM/DD/YYYY HH:mm"),
					"Estimated Final Cost (Last Published)": row?.estimatedFinalCost,
					"Jurisdictional Unit(at Point of Origin)": row?.jurisdictionalUnitAtPoO?.unitIrwinId + " - " + row?.jurisdictionalUnitAtPoO?.name,
					"Irwin Id": row?.irwinId,
				}
				// setting these values to strings so the user doesn't see "null", "undefined", or "Invalid Date" for missing properties.
				row.jurisdictionalAgencyAtPoO ? null : (humanReadableExportData["Jurisdictional Agency(at Point of Origin)"] = "")
				row.jurisdictionalUnitAtPoO ? null : (humanReadableExportData["Jurisdictional Unit(at Point of Origin)"] = "")
				row.fireOutDateTime != null ? null : (humanReadableExportData["Fire Out Date/Time"] = "")
				row.estimatedFinalCost ? null : (humanReadableExportData["Estimated Final Cost (Last Published)"] = "")
				row.incidentSize ? null : (humanReadableExportData["Size (Acres)"] = "")
				return humanReadableExportData
			})
			this.exportSvc.exportArray(exportDataArray, "SearchResults", "WfdssCaseSearch")
		})
	}
}

export const searchObject: ICaseAdvancedSearchFilters = {
	discoveredBetweenMin: null,
	discoveredBetweenMax: null,
	discoveryDateOn: null,
	fireOutBetweenMin: null,
	fireOutBetweenMax: null,
	fireOutDateOn: null,
	fireOutDateNone: false,
	incidentSizeMin: null,
	incidentSizeMax: null,
	irwinId: null,
	estimatedFinalCostMin: null,
	estimatedFinalCostMax: null,
	geographicAreaIds: null,
	jurisdicitonalAgencyAtPooId: null,
	jurisdicitonalUnitId: null,
	protectingUnitId: null,
	uniqueFireIdentifier: null,
	incidentName: null,
	pooStates: null,
}
/**@description Backend interface for searching WfdssCases by provided parameters. Set unused properties to null to ignore those properties in your filter/search critera */
export interface ICaseAdvancedSearchFilters {
	discoveredBetweenMin: number
	discoveredBetweenMax: number
	discoveryDateOn: number
	fireOutBetweenMin: number
	fireOutBetweenMax: number
	fireOutDateOn: number
	fireOutDateNone: boolean
	incidentSizeMin: number
	incidentSizeMax: number
	irwinId: string
	estimatedFinalCostMin: number
	estimatedFinalCostMax: number
	geographicAreaIds: number[]
	jurisdicitonalAgencyAtPooId: number
	jurisdicitonalUnitId: number
	protectingUnitId: number
	uniqueFireIdentifier: string
	incidentName: string
	pooStates: string[]
}

export interface ICaseAdvancedSearchFilterFg {
	discoveredBetweenMin: FormControl<Moment>
	discoveredBetweenMax: FormControl<Moment>
	discoveryDateOn: FormControl<Moment>
	fireOutBetweenMin: FormControl<Moment>
	fireOutBetweenMax: FormControl<Moment>
	fireOutDateOn: FormControl<Moment>
	fireOutDateNone: FormControl<boolean>
	incidentSizeMin: FormControl<number>
	incidentSizeMax: FormControl<number>
	irwinId: FormControl<string>
	estimatedFinalCostMin: FormControl<number>
	estimatedFinalCostMax: FormControl<number>
	geographicAreaIds: FormControl<number>
	jurisdicitonalAgencyAtPooId: FormControl<number>
	jurisdicitonalUnitId: FormControl<number>
	protectingUnitId: FormControl<number>
	uniqueFireIdentifier: FormControl<string>
	incidentName: FormControl<string>
	pooStates: FormControl<string>
}

export interface ICaseAdvancedSearchParams {
	offset: number
	limit: number
	sortDirection: "asc" | "desc" | ""
	sortOnField: string
}
